// Lib
import React, { useEffect, useCallback } from 'react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Utils
import shouldAutoEmbed from '../utils/shouldAutoEmbed';

// Components
import richMediaConnectDecorator from '../richMediaConnectDecorator';
import RichMediaPreviewImage from './RichMediaPreviewImage';
import RichMediaContent from './RichMediaContent';
import RichMediaDragPreview from './RichMediaDragPreview';

import previewImageDropTargetDecorator from '../../dnd/elementDropTargets/previewImageDropTargetDecorator';

const enhance = compose(richMediaConnectDecorator, previewImageDropTargetDecorator());

const RichMedia = (props) => {
    const {
        element,
        className,
        showPreview,
        showRichMedia,
        embeddingRichMedia,
        isPresentational,
        ContentComponent = RichMediaContent,
        PreviewComponent,
        connectFileDropTarget,
        attachment,
        setForceCanDrop,
        isHovered,
        canDrop,
        dispatchRichMediaEmbedding,
    } = props;

    const mediaData = element.getIn(['content', 'media']);
    const imageData = element.getIn(['content', 'image']);

    const onPreviewClick = useCallback(
        (event) => {
            event.stopPropagation();
            dispatchRichMediaEmbedding();
        },
        [dispatchRichMediaEmbedding],
    );

    const forceCanDrop = isHovered && canDrop;
    useEffect(() => {
        setForceCanDrop && setForceCanDrop(forceCanDrop);
    }, [forceCanDrop]);

    let RenderedPreviewComponent = PreviewComponent;
    if (!RenderedPreviewComponent) {
        RenderedPreviewComponent = shouldAutoEmbed(element) ? RichMediaDragPreview : RichMediaPreviewImage;
    }

    const renderContent = (
        <div className={classNames('RichMedia', className)}>
            <ContentComponent {...props} show={showRichMedia && !isPresentational} mediaData={mediaData} />
            <RenderedPreviewComponent
                {...props}
                show={showPreview || isPresentational}
                showLoader={embeddingRichMedia}
                mediaData={mediaData}
                imageData={imageData}
                onClick={onPreviewClick}
                attachment={attachment}
                forceCanDrop={forceCanDrop}
            />
        </div>
    );

    return connectFileDropTarget ? connectFileDropTarget(renderContent) : renderContent;
};

RichMedia.propTypes = {
    element: PropTypes.object.isRequired,
    PreviewComponent: PropTypes.func,
    ContentComponent: PropTypes.func,
    showPreview: PropTypes.bool,
    embeddingRichMedia: PropTypes.bool,
    showRichMedia: PropTypes.bool,
    dispatchRichMediaEmbedding: PropTypes.func,
    className: PropTypes.string,
    isPresentational: PropTypes.bool,
    attachment: PropTypes.object,
    setForceCanDrop: PropTypes.func,
    isHovered: PropTypes.bool,
    canDrop: PropTypes.bool,
    connectFileDropTarget: PropTypes.func,
};

export default enhance(RichMedia);
